import React, { useMemo, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, GanttChartSquare, Flag, Box, CheckSquare, Info } from "lucide-react";
import { Card, fmtDate } from "./shared";

const KIND_LABEL = { wbs: "Fază / WBS", task: "Activitate", milestone: "Jalon" };

export function SchedulePanel({ pid, cfg, reload }) {
  const sched = cfg.schedule || null;
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pub, setPub] = useState(() => new Set((sched?.tasks || []).filter((t) => t.published).map((t) => t.id)));

  const tasks = sched?.tasks || [];
  const publishedCount = pub.size;

  async function onFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.xml$/i.test(file.name)) { toast.error("Încarcă un fișier Primavera P6 în format XML (.xml)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/staff/client-config/${pid}/schedule`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPub(new Set((data.tasks || []).filter((t) => t.published).map((t) => t.id)));
      toast.success(`Grafic importat — ${data.activity_count} activități`);
      reload();
    } catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); }
  }

  async function del() {
    if (!window.confirm("Ștergi graficul de execuție? Nu va mai fi vizibil clientului.")) return;
    try { await api.delete(`/staff/client-config/${pid}/schedule`); toast.success("Grafic șters"); reload(); }
    catch (err) { toast.error(apiErr(err)); }
  }

  async function savePublish() {
    setSaving(true);
    try {
      await api.put(`/staff/client-config/${pid}/schedule/publish`, { published_ids: Array.from(pub) });
      toast.success(`Publicat — clientul vede ${pub.size} elemente`);
      reload();
    } catch (err) { toast.error(apiErr(err)); } finally { setSaving(false); }
  }

  const toggle = (id) => setPub((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const setBy = (fn) => setPub(new Set(tasks.filter(fn).map((t) => t.id)));

  if (!sched) {
    return (
      <Card className="p-8" data-testid="cc-schedule">
        <div className="max-w-lg">
          <div className="flex items-center gap-2 text-ink font-display font-semibold text-[17px]"><GanttChartSquare className="h-5 w-5 text-brand" /> Grafic de execuție</div>
          <p className="text-[13px] text-ink-muted mt-1.5">Încarcă graficul de execuție exportat din <b>Primavera P6</b> în format <b>XML</b>. Vei alege apoi ce activități se publică, iar clientul le va vedea ca diagramă Gantt în tab-ul „Calendar”.</p>
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="cc-schedule-upload"
            className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 bg-ink text-white text-sm rounded-lg hover:bg-brand disabled:opacity-60">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Încarcă fișier P6 XML
          </button>
          <p className="text-[11px] text-ink-muted mt-2">În Primavera P6: File → Export → Primavera P6 (XML). Max 30MB.</p>
        </div>
        <input ref={fileRef} type="file" accept=".xml,text/xml,application/xml" onChange={onFile} className="hidden" />
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="cc-schedule">
      <Card className="p-5">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="h-10 w-10 rounded-lg bg-brand/10 flex items-center justify-center shrink-0"><GanttChartSquare className="h-5 w-5 text-brand" /></div>
          <div className="min-w-0 flex-1">
            <div className="font-display font-semibold text-ink">{sched.project_name || "Grafic de execuție"}</div>
            <div className="text-[12px] text-ink-muted mt-0.5">
              {sched.source_name} · {sched.activity_count} activități · importat {fmtDate(sched.uploaded_at)}{sched.data_date ? ` · Data Date ${fmtDate(sched.data_date)}` : ""}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="cc-schedule-reupload" className="inline-flex items-center gap-1.5 px-3 py-2 text-[13px] border border-neutral-300 rounded-lg hover:bg-neutral-50 disabled:opacity-60">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Înlocuiește
            </button>
            <button onClick={del} data-testid="cc-schedule-delete" className="inline-flex items-center gap-1.5 px-3 py-2 text-[13px] border border-red-200 text-red-600 rounded-lg hover:bg-red-50"><Trash2 className="h-4 w-4" /> Șterge</button>
          </div>
        </div>
        <input ref={fileRef} type="file" accept=".xml,text/xml,application/xml" onChange={onFile} className="hidden" />
      </Card>

      <Card className="p-5">
        <div className="flex items-center gap-2 text-[13px] text-ink-muted mb-3"><Info className="h-4 w-4" /> Alege ce se publică. Restul rămâne intern.</div>
        <div className="flex flex-wrap gap-2 mb-4" data-testid="cc-schedule-quick">
          <QuickBtn onClick={() => setBy((t) => t.level <= 1)}>Nivel ≤ 1</QuickBtn>
          <QuickBtn onClick={() => setBy((t) => t.level <= 2)}>Nivel ≤ 2</QuickBtn>
          <QuickBtn onClick={() => setBy((t) => t.level <= 3)}>Nivel ≤ 3</QuickBtn>
          <QuickBtn onClick={() => setBy(() => true)}>Toate</QuickBtn>
          <QuickBtn onClick={() => setBy((t) => t.kind === "milestone")}>Doar jaloane</QuickBtn>
          <QuickBtn onClick={() => setBy(() => false)}>Nimic</QuickBtn>
        </div>

        <div className="border border-neutral-200 rounded-lg divide-y divide-neutral-100 max-h-[520px] overflow-auto" data-testid="cc-schedule-tree">
          {tasks.map((t) => (
            <label key={t.id} data-testid={`cc-schedule-row-${t.id}`}
              className="flex items-center gap-3 px-3 py-2 hover:bg-neutral-50 cursor-pointer text-[13px]">
              <input type="checkbox" checked={pub.has(t.id)} onChange={() => toggle(t.id)} className="h-4 w-4 accent-brand shrink-0" />
              <span style={{ paddingLeft: `${(t.level - 1) * 18}px` }} className="flex items-center gap-1.5 min-w-0 flex-1">
                {t.kind === "wbs" ? <Box className="h-3.5 w-3.5 text-brand shrink-0" /> : t.kind === "milestone" ? <Flag className="h-3.5 w-3.5 text-amber-500 shrink-0" /> : <span className="h-1.5 w-1.5 rounded-full bg-neutral-300 shrink-0 ml-1" />}
                <span className={`truncate ${t.kind === "wbs" ? "font-semibold text-ink" : "text-ink"}`}>{t.name}</span>
              </span>
              <span className="text-[11px] text-ink-muted whitespace-nowrap hidden sm:inline">{fmtDate(t.start)}{t.finish && t.finish !== t.start ? ` → ${fmtDate(t.finish)}` : ""}</span>
              <span className="text-[11px] tabular-nums text-ink-muted w-10 text-right">{Math.round(t.percent || 0)}%</span>
            </label>
          ))}
        </div>

        <div className="flex items-center justify-between mt-4">
          <span className="text-[12px] text-ink-muted"><CheckSquare className="h-3.5 w-3.5 inline mr-1" />Clientul va vedea <b className="text-ink">{publishedCount}</b> din {tasks.length} elemente</span>
          <button onClick={savePublish} disabled={saving} data-testid="cc-schedule-save"
            className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-white text-sm rounded-lg hover:bg-brand disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckSquare className="h-4 w-4" />} Salvează publicarea
          </button>
        </div>
      </Card>
    </div>
  );
}

function QuickBtn({ onClick, children }) {
  return <button onClick={onClick} className="px-3 py-1.5 text-[12px] font-medium border border-neutral-300 rounded-md hover:bg-neutral-50 text-ink-muted hover:text-ink">{children}</button>;
}
